import React, {useCallback, useMemo, useRef} from "react";
import {useSiren} from "../SirenContext";
import {useGetMassWarningTemplates} from "../hooks/useGetMassWarningTemplates";
import {MassWarningTemplate} from "../typedefs";
import {useNavigateWithClientID} from "../hooks/useNavigateWithClientID";
import {useAuth} from "../../../context/user";
import {Action, ActionSelect} from "../../../components/ui/ActionSelect";
import {usePlatformDeleteModal} from "../hooks/useDeleteModal";
import {UserName} from "../../../components/data/UserName";
import {PaginatedSelector} from "../../../components/paginated-selector/paginated-selector";
import moment from "moment";
import {usePermissions} from "@/context/modulePermissions";

type TemplateListProps = {
    type: string
}

export function TemplateList({type}: TemplateListProps) {
    const {clientID} = useSiren()
    const {isSuperAdmin} = useAuth()
    const {navigateWithClientID} = useNavigateWithClientID()
    const getItems = useGetMassWarningTemplates(type, clientID)
    const refetchRef = useRef<(() => void) | null>(null)
    const {hasPermission, hasFullAccess} = usePermissions();

    const readOnly = useMemo(() => {
        return hasFullAccess("siren_resources")
    }, [hasFullAccess])

    const getColumns = useCallback((t: MassWarningTemplate) => [
        t.name,
        t.category ?? "",
        <UserName id={t.createdBy}/>,
        <UserName id={t.updatedBy}/>,
        moment(t.updatedAt).format("DD/MM/YYYY HH:mm"),
    ], [])


    const renderActions = useCallback((t: MassWarningTemplate) => (
        readOnly ? <div></div> : <TemplateActions template={t} refetch={() => refetchRef.current?.()}/>
    ), [readOnly])

    if (!clientID && isSuperAdmin) return null;

    return (
        <PaginatedSelector
            key={clientID}
            headers={["Name", "Category", "Created By", "Last Updated By", "Last Updated"]}
            getItems={getItems}
            getColumns={getColumns}
            disableSelect={true}
            renderActions={renderActions}
            onColumnClick={(t) => navigateWithClientID(`siren/templates/${t.id}/edit`)}
            syncWithUrl={true}
            onRefetchReady={(refetch) => { refetchRef.current = refetch }}
        />
    )
}

function TemplateActions({template, refetch}: { template: MassWarningTemplate; refetch: () => void }) {
    const {navigateWithClientID} = useNavigateWithClientID()

    const {DeleteModal, openDeleteModal} = usePlatformDeleteModal(
        "Template",
        template.name,
        `api/siren/templates/${template.id}`,
        refetch,
    )

    return (
        <>
            <DeleteModal/>
            <ActionSelect label="Actions">
                <Action label="Edit" onSelect={() => navigateWithClientID(`siren/templates/${template.id}/edit`)}/>
                <Action label="Delete" onSelect={openDeleteModal}/>
            </ActionSelect>
        </>
    )
}